import {
  NavLink,
  Stack,
  Text,
  Badge,
  Divider,
  Group,
  ThemeIcon,
  Box,
} from '@mantine/core';
import {
  IconDashboard,
  IconCloud,
  IconDeviceDesktop,
  IconBuilding,
  IconDoor,
  IconPackage,
  IconEye,
  IconListCheck,
  IconInfoCircle,
  IconSettings,
  IconUsers,
  IconTerminal2,
  IconPhoto,
  IconPlayerPlay,
  IconActivityHeartbeat,
  IconStack2,
  IconUserShield,
} from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
  badgeColor?: string;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    icon: <IconDashboard size={20} />,
    path: '/dashboard',
  },
  {
    label: 'Devices',
    icon: <IconDeviceDesktop size={20} />,
    path: '/devices',
  },
  {
    label: 'Groups',
    icon: <IconUsers size={20} />,
    path: '/groups',
  },
  {
    label: 'FOG',
    icon: <IconCloud size={20} />,
    path: '/fog',
    children: [
      {
        label: 'Dashboard',
        icon: <IconDashboard size={16} />,
        path: '/fog',
      },
      {
        label: 'Hosts',
        icon: <IconDeviceDesktop size={16} />,
        path: '/fog/hosts',
      },
      {
        label: 'Images',
        icon: <IconPhoto size={16} />,
        path: '/fog/images',
      },
      {
        label: 'Tasks',
        icon: <IconPlayerPlay size={16} />,
        path: '/fog/tasks',
      },
    ],
  },
  {
    label: 'Sites',
    icon: <IconBuilding size={20} />,
    path: '/sites',
  },
  {
    label: 'Rooms',
    icon: <IconDoor size={20} />,
    path: '/rooms',
  },
  {
    label: 'Inventory',
    icon: <IconPackage size={20} />,
    path: '/inventory',
  },
  {
    label: 'Preview',
    icon: <IconEye size={20} />,
    path: '/preview',
  },
  {
    label: 'Tasks',
    icon: <IconListCheck size={20} />,
    path: '/tasks',
  },
  {
    label: 'Scans',
    icon: <IconTerminal2 size={20} />,
    path: '/scans',
  },
  {
    label: 'Queue',
    icon: <IconStack2 size={20} />,
    path: '/queue',
  },
  {
    label: 'Status',
    icon: <IconActivityHeartbeat size={20} />,
    path: '/status',
  },
  {
    label: 'AD Users',
    icon: <IconUserShield size={20} />,
    path: '/ad-users',
  },
  {
    label: 'Settings',
    icon: <IconSettings size={20} />,
    path: '/settings',
  },
];

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();

  const isItemActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some(child => location.pathname === child.path);
    }
    return location.pathname === item.path;
  };

  const renderNavItem = (item: NavItem, index: number) => {
    if (item.children) {
      return (
        <NavLink
          key={item.path}
          label={item.label}
          leftSection={item.icon}
          rightSection={
            item.badge ? (
              <Badge size="xs" color={item.badgeColor} variant="filled">
                {item.badge}
              </Badge>
            ) : null
          }
          active={isItemActive(item)}
          defaultOpened={isItemActive(item)}
          variant="filled"
          className="nav-item"
          styles={{
            root: {
              borderRadius: 'var(--mantine-radius-md)',
              transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
              animationDelay: `${index * 50}ms`,
            },
            children: {
              paddingLeft: '0.75rem',
            },
          }}
        >
          {item.children.map((child, childIndex) => (
            <NavLink
              key={child.path}
              label={child.label}
              leftSection={child.icon}
              active={location.pathname === child.path}
              onClick={() => navigate(child.path)}
              variant="filled"
              styles={{
                root: {
                  borderRadius: 'var(--mantine-radius-md)',
                  transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
                  animationDelay: `${(index + childIndex) * 30}ms`,
                  '&:hover': {
                    transform: 'translateX(4px)',
                  },
                },
              }}
            />
          ))}
        </NavLink>
      );
    }

    return (
      <NavLink
        key={item.path}
        label={item.label}
        leftSection={item.icon}
        rightSection={
          item.badge ? (
            <Badge size="xs" color={item.badgeColor} variant="filled">
              {item.badge}
            </Badge>
          ) : null
        }
        active={location.pathname === item.path}
        onClick={() => navigate(item.path)}
        variant="filled"
        className="nav-item"
        styles={{
          root: {
            borderRadius: 'var(--mantine-radius-md)',
            transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
            animationDelay: `${index * 50}ms`,
            '&:hover': {
              transform: 'translateX(4px)',
            },
            '&:active': {
              transform: 'translateX(2px) scale(0.98)',
            },
          },
          label: {
            transition: 'all 200ms ease',
          },
        }}
      />
    );
  };

  return (
    <Stack h="100%" justify="space-between" p="md">
      {/* Navigation items */}
      <Stack gap="xs">
        <Text size="xs" fw={500} c="dimmed" tt="uppercase" mb="xs">
          Navigation
        </Text>

        {navItems.map((item, index) => renderNavItem(item, index))}
      </Stack>

      {/* Footer info */}
      <Box>
        <Divider my="sm" />
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon variant="light" size="sm" color="accent">
            <IconInfoCircle size={14} />
          </ThemeIcon>
          <Text size="xs" c="dimmed">
            DeploySense
          </Text>
        </Group>
        <Text size="xs" c="dimmed" mt={4}>
          v1.0.0
        </Text>
      </Box>
    </Stack>
  );
}
